import React, {useCallback, useEffect, useMemo, useState} from "react";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col} from "reactstrap";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import {numComparator} from "../../../../core/services/utils/utils";
import {ReactSortable} from "react-sortablejs";
import TaskStatusCard from "../../app-specific/task-status-card";
import {TaskStatusTypes} from "../../../../core/constants/enums";


const schema = Yup.object().shape({
    newTitle: Yup.string().nullable().required(ValidateMessages.required)
});

const UpsertTaskStatusesDialog = ({open, close, onFinish}) => {
    const [statuses, _setStatuses] = useState([])
    const [loading, setLoading] = useState(false)
    const [updating, setUpdating] = useState(false);
    const [creating, setCreating] = useState(false)
    const isMounted = useIsMounted()
    const validate = makeValidate(schema);
    const required = makeRequired(schema);

    const todoStatus = useMemo(() => statuses?.find(e => e.statusType === TaskStatusTypes.ToDo), [statuses])
    const inProgressStatuses = useMemo(() => statuses.filter(e => e.statusType === TaskStatusTypes.InProgress) ?? [], [statuses])
    const doneStatus = useMemo(() => statuses?.find(e => e.statusType === TaskStatusTypes.Done), [statuses])

    /**
     * Fetches the task statuses from the server.
     * @type {(function(): Promise<*>)}
     */
    const getStatuses = useCallback(async () => {
        setLoading(true)
        const response = await Api.getTaskStatuses()
        if (!isMounted()) return
        if (response?.resultFlag) {
            setStatuses(response?.data?.map(e => ({
                ...e,
                editing: false,
                inEditMode: false,
                filtered: e.statusType === TaskStatusTypes.Done || e.statusType === TaskStatusTypes.ToDo
            })) ?? [])
        }
        setLoading(false)
    }, [isMounted]);

    /**
     * Listens for the changes in the open state of this dialog and with each change:
     * if the dialog is opened, fetches the statuses from the server.
     */
    useEffect(() => {
        if (open) {
            getStatuses().then();
        }
    }, [open, getStatuses])


    /**
     * Sets the categories state of this component by sorting them based on their orderIndex after the new state is
     * received.
     *
     * This method acts as a wrapper that injects the sorting behaviour at the end of the setting this state.
     * @param {any[] | function(any[]): any[]} stateOrCallback
     * @param {boolean} sort whether to sort the categories or not. used when rearranging the categories in sortablejs
     */
    const setStatuses = (stateOrCallback, sort = true) => {
        _setStatuses((prevState) => {
            let newState;
            if (typeof stateOrCallback === 'function') {
                newState = stateOrCallback(prevState)
            } else {
                newState = stateOrCallback
            }
            if (sort) {
                return newState?.sort((a, b) => numComparator(a.orderIndex, b.orderIndex)) ?? []
            }
            return newState
        })
    }

    /**
     * Create a new category with the given values and adds it to the list of categories.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<boolean>}
     */
    const createStatus = async (values, form) => {
        setCreating(true)
        const forApi = {
            title: values.newTitle,
        }
        const response = await Api.createTaskStatus(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setStatuses(response?.data?.map(e => ({
                ...e,
                editing: false,
                inEditMode: false,
                filtered: e.statusType === TaskStatusTypes.Done || e.statusType === TaskStatusTypes.ToDo
            })) ?? [])
            form.change('newTitle', '')
            form.resetFieldState('newTitle');
        }
        setCreating(false)
        return false
    }

    /**
     * Updates a status with the given values.
     * @param {any} status the status to be updated.
     * @return {Promise<boolean>}
     */
    const updateStatus = async (status) => {
        setStatuses(prevState => prevState.map(e => e.id === status.id ? {
            ...e,
            editing: true
        } : e))
        const forApi = {
            ...status,
            updatedProperties: ['Title'],
        }
        const response = await Api.updateTaskStatus(forApi)
        if (!isMounted())
            return false
        setStatuses(prevState => {
            const newList = prevState.map(e => e.id === status.id
                ? {
                    ...e,
                    editing: false,
                    ...(response?.resultFlag ? forApi : {})
                }
                : e)
            return newList.sort((a, b) => numComparator(a.orderIndex, b.orderIndex))
        })
        return response?.resultFlag ?? false
    }

    /**
     * Removes a status from the server and then removes it from the local list.
     * @param {any} status the status to be removed
     */
    const removeStatus = async (status) => {
        setStatuses(prevState => prevState.map(e => e.id === status.id ? {
            ...e,
            editing: true
        } : e))
        const response = await Api.removeTaskStatus(status.id);
        if (!isMounted())
            return
        if (response?.resultFlag) {
            setStatuses(response?.data?.map(e => ({
                ...e,
                editing: false,
                inEditMode: false,
                filtered: e.statusType === TaskStatusTypes.Done || e.statusType === TaskStatusTypes.ToDo
            })) ?? [])
        } else {
            setStatuses(prevState => prevState.map(e => e.id === status.id ? {
                ...e,
                editing: false
            } : e))
        }
    }

    /**
     * Updates the statuses' orderIndex from the server.
     *
     * Note: This api is called after the dialog is closed, and thus its response is almost ignored.
     * @return {Promise<any[]>} the categories
     */
    const updateStatusesOrder = async () => {
        if (inProgressStatuses.length === 0)
            return statuses;
        setUpdating(true);
        const forApi = {
            statusIds: inProgressStatuses?.map(e => e.id) ?? [],
        }
        const response = await Api.reorderTaskStatuses(forApi)
        if (!isMounted())
            return []

        setUpdating(false);
        if (response?.resultFlag) {
            return statuses.map((e, i) => ({...e, orderIndex: i + 1}))
        }
        return statuses
    }

    /**
     * Closes the dialog by passing the new statuses to the calling component.
     */
    const onClose = async () => {
        setLoading(true);
        const statuses = await updateStatusesOrder();
        if (!isMounted())
            return;
        setLoading(false);
        onFinish(statuses)
        close();
    }

    /**
     * Contains The new list after being rearranged and sorted.
     * @param {any[]} newStatuses
     */
    const rearrangeStatuses = (newStatuses) => {
        const newArray = [];
        if (todoStatus) {
            newArray.push(todoStatus)
        }
        newArray.push(...newStatuses)
        if (doneStatus) {
            newArray.push(doneStatus)
        }
        setStatuses(newArray, false)
    }

    /**
     * Toggles the edit mode of the status cards.
     * @param {any} status
     * @param {boolean} inEditMode
     */
    const toggleStatusEditMode = (status, inEditMode = false) => {
        setStatuses(prevState => prevState.map(e => e.id === status.id ? {...e, inEditMode} : e))
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={onClose}
                maxWidth={'md'}
                loading={loading || creating || updating}
                disableBackdropClick={updating}
                disableEscapeKeyDown={updating}
                className={'upsert-notification-category-dialog bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Manage Task Statuses
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={''}>
                    <Col xs={12}>
                        <p className={'text-md font-weight-semi-bold mt-2 mb-4'}>
                            Create a new Status
                        </p>
                    </Col>
                    <Form
                        className='d-flex flex-wrap w-100 align-items-center'
                        onSubmit={createStatus}
                        validate={validate}
                        initialValues={{}}
                        render={({values, form}) => {
                            return (
                                <>
                                    <Col className={'pl-3'}>
                                        <OutlinedInputForm
                                            name={'newTitle'}
                                            placeholder={'title'}
                                            label={"New Title"}
                                            required={required.newTitle}
                                            value={values.newTitle ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={'auto'}>
                                        <Button
                                            className='py-2-5 px-5'
                                            type={'submit'}
                                            variant={'contained'}
                                            color={'primary'}
                                            disabled={creating}
                                        >
                                            {
                                                creating
                                                    ? "Adding..."
                                                    : "Add"
                                            }
                                        </Button>
                                    </Col>
                                </>
                            )
                        }}
                    />
                    <Col xs={12} className={'mt-3 d-flex flex-wrap mb-5'}>
                        {
                            loading
                                ? Array(4).fill(null).map((e, i) => (
                                    <div key={i} className={'loading normal w-100 mb-3'}>
                                        <div/>
                                    </div>
                                ))
                                : <>
                                    {
                                        todoStatus &&
                                        <TaskStatusCard
                                            status={todoStatus}
                                            update={updateStatus}
                                            remove={removeStatus}
                                            setEditing={(e) => toggleStatusEditMode(todoStatus, e)}
                                            sortable={false}
                                            removable={false}
                                        />
                                    }
                                    <ReactSortable
                                        className={'categories-container'}
                                        list={inProgressStatuses ?? []}
                                        sort={true}
                                        setList={rearrangeStatuses}
                                        handle={'.handle'}
                                        group={'categories'}
                                        animation={210}
                                        forceFallback={false}
                                        swapThreshold={0.65}
                                        ghostClass={"dragged-category"}
                                    >
                                        {
                                            inProgressStatuses?.map(status => (
                                                <TaskStatusCard
                                                    key={status.id}
                                                    status={status}
                                                    update={updateStatus}
                                                    remove={removeStatus}
                                                    setEditing={(e) => toggleStatusEditMode(status, e)}
                                                    sortable={!statuses?.some(e => !!e.inEditMode)}
                                                    removable
                                                />
                                            ))
                                        }
                                    </ReactSortable>
                                    {
                                        doneStatus &&
                                        <TaskStatusCard
                                            status={doneStatus}
                                            update={updateStatus}
                                            remove={removeStatus}
                                            setEditing={(e) => toggleStatusEditMode(doneStatus, e)}
                                            sortable={false}
                                            removable={false}
                                        />
                                    }
                                </>
                        }
                    </Col>
                </DialogContent>
                <DialogActions/>
            </Dialog>
        </>
    )
}

export default UpsertTaskStatusesDialog;
